import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { all, put, takeEvery, takeLatest } from "redux-saga/effects";
import { BASE_URL } from "../../providers/api";
import { fileUploadVideo, fileUploadComplete, fileUploadError, roomVideoRemoveFailure, roomVideoRemoveRequest, roomVideoRemoveSuccess, roomVideoSaveRequest, roomVideoSaveSuccess, roomVideosRequest, roomVideosRequestFailure, roomVideosRequestSuccess, blobFailure, blobRequest, blobSuccess } from "./slices";
import { IUploadRequest, IUploadedInfoResponse, IVideoDeleteResponse, IVideoSaveResponse, IVideos, IVideosResponse } from "./types";
import { fileDownloadService, fileUploadService, roomVideoDeleteRequestService, roomVideoSaveRequestService, roomVideosListRequestService } from "./services";


// *** LIST VIDEOS ***
function* roomRequestVideosSaga(action: PayloadAction<{ roomId: string }>) {
    try {
        const videosResponse: AxiosResponse<IVideosResponse> = yield roomVideosListRequestService(action.payload.roomId);
        if (videosResponse.data.status) {
            const videosData: IVideos[] = videosResponse.data.data?.map(videos => ({
                id: videos.id,
                link: `${BASE_URL}download/?type=roomvideo&id=${videos.id}`,
                dateCaptured: new Date(videos.capturedAt),
                fileName: ''
            })) || []

            yield put(roomVideosRequestSuccess({
                roomId: action.payload.roomId,
                data: videosData
            }));
        }
        else {
            yield put(roomVideosRequestFailure({
                roomId: action.payload.roomId,
                error: {
                    message: videosResponse.data.message || 'Error trying to get the list of videos'
                }
            }));
        }
    }
    catch (error) {
        yield put(roomVideosRequestFailure({
            roomId: action.payload.roomId,
            error: {
                message: 'Error trying to get the list of videos'
            }
        }));
    }
}

function* watchroomRequestVideosSaga() {
    yield takeLatest(roomVideosRequest.toString(), roomRequestVideosSaga);
}

// *** DELETE ***
function* roomDeleteVideoSaga(action: PayloadAction<{
    roomId: string;
    videoId: string;
}>) {
    try {
        const videoDeleteResponse: AxiosResponse<IVideoDeleteResponse> = yield roomVideoDeleteRequestService(
            action.payload.videoId
        );
        if (videoDeleteResponse.data.status) {
            yield put(roomVideoRemoveSuccess({
                roomId: action.payload.roomId,
                videoId: action.payload.videoId
            }))
        } else {
            yield put(roomVideoRemoveFailure({
                roomId: action.payload.roomId,
                videoId: action.payload.videoId,
                error: {
                    message: videoDeleteResponse.data.message || 'Error removing video'
                }
            }));
        }
    } catch (error: any) {
        yield put(roomVideoRemoveFailure({
            roomId: action.payload.roomId,
            videoId: action.payload.videoId,
            error: {
                message: error.response.data.message || 'Error trying to get the list of video'
            }
        }));
    }
}
function* watchDeleteVideoRequestSaga() {
    yield takeLatest(roomVideoRemoveRequest.toString(), roomDeleteVideoSaga);
}

// *** BLOB  ***
function* blobRequestSaga(action: PayloadAction<{
    link: string;
}>) {
    try {
        const fileBlob: AxiosResponse<Blob> = yield fileDownloadService(action.payload.link)

        yield put(blobSuccess({
            link: action.payload.link,
            blob: fileBlob.data
        }))
    }
    catch (error: any) {
        yield put(blobFailure({
            link: action.payload.link,
            error: {
                message: error.response.data.message || 'Error trying to download file'
            }
        }));
    }
}

function* watchBlobRequestSaga() {
    yield takeEvery(blobRequest.toString(), blobRequestSaga);
}
// FILE UPLOAD
function* fileUploadSaga(action: PayloadAction<IUploadRequest>) {
    try {
        const uploadResponse: AxiosResponse<IUploadedInfoResponse> = yield fileUploadService(
            action.payload.id,
            action.payload.data
        );

        if (uploadResponse.data.status) {
            const [uploadedInfo] = uploadResponse.data.data;

            if (action.payload.callback && uploadedInfo) action.payload.callback({
                ...uploadedInfo,
                uploadId: action.payload.id
            })

        }
    }
    catch (error: any) {
        yield put(fileUploadError({
            uploadId: action.payload.id,
            error: {
                message: error.response.data.message || 'Error trying to upload file'
            }
        }));
    }
}

function* watchFileUploadSaga() {
    yield takeLatest(fileUploadVideo.toString(), fileUploadSaga);
}

// *** ROOM VIDEO SAVE ***
function* roomVideoSaveRequestSaga(action: PayloadAction<{
    uploadId: string;
    path: string;
    roomId: string;
}>) {
    try {
        const saveResponse: AxiosResponse<IVideoSaveResponse> = yield roomVideoSaveRequestService(
            action.payload.roomId,
            action.payload.path
        );

        if (saveResponse.data.status) {
            const data = saveResponse.data.data;


            yield put(fileUploadComplete({ uploadId: action.payload.uploadId }));
            yield put(roomVideoSaveSuccess({
                roomId: action.payload.roomId,
                video: {
                    fileName: "",
                    id: data.id,
                    link: `${BASE_URL}download/?type=roomvideo&id=${data.id}`
                }
            }));
        }
    }
    catch (error: any) {
        yield put(fileUploadError({
            uploadId: action.payload.uploadId,
            error: {
                message: error.response.data.message || 'Error trying to save room video'
            }
        }));
    }
}

function* watchRoomVideoSaveRequestSaga() {
    yield takeLatest(roomVideoSaveRequest.toString(), roomVideoSaveRequestSaga);
}
export default all([
    watchroomRequestVideosSaga(),
    watchBlobRequestSaga(),
    watchDeleteVideoRequestSaga(),
    watchFileUploadSaga(),
    watchRoomVideoSaveRequestSaga()
])